import { useState, useEffect, useRef, memo } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import PropTypes from "prop-types";
import HeroOverlay from "./HeroOverlay";

function Hero({ heroImage }) {
  const { t } = useTranslation();
  const heroData = t("components.home.hero", { returnObjects: true });

  const heroSectionRef = useRef(null);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!heroSectionRef.current) return;
      const scrollY = window.scrollY;
      const threshold = window.innerHeight / 5;
      setIsOverlayVisible(scrollY > threshold);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section
      ref={heroSectionRef}
      className="relative h-[150vh] landscape:max-lg:h-[200vh] hero-section overflow-hidden bg-black"
    >
      <img
        src={heroImage}
        alt="Hero background"
        className="absolute inset-0 w-full h-full object-cover z-0 hidden"
        onLoad={() => setImageLoaded(true)}
        loading="eager"
        fetchPriority="high"
      />

      {imageLoaded && (
        <>
          <motion.img
            src={heroImage}
            alt="Hero background"
            className="absolute inset-0 w-full h-full object-cover z-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          />

          <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/70 to-black/60 z-10" />

          <motion.div
            className="absolute top-[15%] left-0 right-0 text-white px-4 sm:px-6 md:px-12 z-20 mx-auto max-w-[90%] sm:max-w-[800px]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          >
            <div className="text-center">
              <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl 2xl:text-5xl font-bold mb-6 leading-tight">
                {heroData.title}
              </h1>
              <p className="text-md sm:text-lg md:text-xl lg:text-2xl text-gray-300 leading-relaxed mb-6">
                {heroData.subtitle}
              </p>
              <p className="text-md sm:text-lg md:text-xl lg:text-2xl font-bold mb-4">
                {heroData.tagline}
              </p>
            </div>
          </motion.div>

          {isOverlayVisible && (
            <motion.div
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="absolute bottom-[10%] left-1/2 transform -translate-x-1/2 w-full flex justify-center z-20"
            >
              <HeroOverlay isVisible={isOverlayVisible} />
            </motion.div>
          )}
        </>
      )}
    </section>
  );
}

Hero.propTypes = {
  heroImage: PropTypes.string.isRequired,
};

export default memo(Hero);
